import './index.scss';
import React, { useCallback, useState, useRef, useEffect, forwardRef, Ref, useImperativeHandle } from 'react';
import { If } from 'common/src/components';
import { classSet } from '@umu/umu-dom';
import { Iconfont } from '@umu/umu-design';
import { searchAccount } from '@umu/umu-api';
import { getUniqueList, isSame, debounce, keyboardEvent } from '@umu/umu-util';
import { useMethods, usePrev } from '@umu/umu-react-hooks';
import { AccountItem } from './userItem';
import { SelectedItem } from './selectedItem';

import { Account, SELECTED_CARD, IProps } from '../../type';
export * from '../../type';
export type RefType = { setInputValue: (value: SELECTED_CARD[]) => void };

const defaultFilter = (account: Account) => !!account.account_type;
const getAccountKey = (account: SELECTED_CARD) => {
    return `${account.account_type}_${account.account}_${account.id}`;
};

export const MemberSearchInput = forwardRef((props: IProps, ref: Ref<RefType>) => {
    const {
        autoFocus,
        placeholder,
        searchSource,
        paramExtend = {},
        hasCleanBtn = false,
        filter = defaultFilter,
        onChange,
        onDelete,
        doSearch,
        onResultChange,
        defaultValue = [],
    } = props;
    const [query, setQuery] = useState<string>('');
    const [inFocus, setInFocus] = useState(!!autoFocus);
    const [searchResults, setSearchResults] = useState<Account[]>([]);
    const [selectedCards, setSelectedCards] = useState<SELECTED_CARD[]>(defaultValue);
    const queryRef = useRef(query);
    queryRef.current = query;
    const firstRender = useRef(true);
    const isComposing = useRef(false);
    const inputDom = useRef<HTMLInputElement | null>(null);
    const prevSelected = usePrev(selectedCards);

    useEffect(() => {
        setSelectedCards(defaultValue);
    }, [defaultValue]);

    useEffect(() => {
        onResultChange && onResultChange(searchResults);
    }, [searchResults]);

    useEffect(() => {
        // 如果是第一次渲染，没必要触发 onChange
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        if (isSame(prevSelected, selectedCards)) {
            return;
        }
        onChange(
            getUniqueList(
                selectedCards,
                (a: SELECTED_CARD, b: SELECTED_CARD) => `${a.account_type}${a.id}` === `${b.account_type}${b.id}`,
            ),
        );
    }, [selectedCards]);

    useImperativeHandle(ref, () => ({
        setInputValue(value: SELECTED_CARD[]) {
            setSelectedCards(value);
        },
    }));

    const getPreSelectIdx = () => {
        return searchResults.findIndex((user) => !!user.preSelect);
    };

    const searchUsers = useCallback(
        debounce(() => {
            const keyword = queryRef.current;
            if (keyword.length < 2) {
                setSearchResults([]);
                return;
            }
            const params = Object.assign({ accounts: keyword, search_source: searchSource }, paramExtend);
            searchAccount(params).then((users: Account[]) => {
                const usersData = (users || []).filter(filter);
                usersData[0] && (usersData[0].preSelect = true);
                setSearchResults(usersData);
            });
        }, 400),
        [selectedCards],
    );

    const {
        onCompositionEnd,
        onKeyboardEnterDown,
        onKeyboardArrowUpDown,
        onKeyboardArrowDownDown,
        onKeyboardBackspaceDown,
    } = useMethods({
        onCompositionEnd() {
            isComposing.current = false;
            // 中文输入法输入完成后，触发搜索
            searchUsers();
        },
        onKeyboardBackspaceDown() {
            if (!query) {
                const restLen = selectedCards.length - 1;
                onDelete && onDelete([selectedCards[selectedCards.length - 1]]);
                setSelectedCards(selectedCards.slice(0, restLen));
            }
        },
        onKeyboardEnterDown() {
            const trimQuery = query.trim();
            // 输入框没有内容，回车触发搜索
            if (!trimQuery) {
                !!selectedCards.length && doSearch && doSearch(selectedCards);
                return;
            }
            if (isComposing.current) {
                return;
            }
            // 选择搜索账户
            const preSelectIdx = getPreSelectIdx();
            if (preSelectIdx !== -1) {
                handleSelectName(searchResults[preSelectIdx]);
                return;
            }
            // 选中关键词
            setSelectedCards([
                ...selectedCards,
                {
                    id: '',
                    account: trimQuery,
                    account_type: 'keyword',
                },
            ]);
            setQuery('');
            setSearchResults([]);
        },
        onKeyboardArrowDownDown() {
            if (!searchResults.length) {
                return;
            }
            const newResults = [...searchResults];
            const currentIdx = getPreSelectIdx();
            const nextIdx = currentIdx + 1 >= newResults.length ? 0 : currentIdx + 1;
            newResults[currentIdx] && (newResults[currentIdx].preSelect = false);
            newResults[nextIdx].preSelect = true;
            setSearchResults(newResults);
        },
        onKeyboardArrowUpDown() {
            if (!searchResults.length) {
                return;
            }
            const newResults = [...searchResults];
            const currentIdx = getPreSelectIdx();
            if (currentIdx === -1) {
                return;
            }
            const nextIdx = currentIdx - 1;
            newResults[currentIdx] && (newResults[currentIdx].preSelect = false);
            newResults[nextIdx] && (newResults[nextIdx].preSelect = true);
            setSearchResults(newResults);
        },
    });

    const inputRef = useCallback((node: HTMLInputElement) => {
        if (node) {
            inputDom.current = node;
            keyboardEvent.enterDown(node, onKeyboardEnterDown);
            keyboardEvent.backspaceDown(node, onKeyboardBackspaceDown);
            keyboardEvent.arrowDownDown(node, onKeyboardArrowDownDown);
            keyboardEvent.arrowUpDown(node, onKeyboardArrowUpDown, { preventDefault: true });
        }
    }, []);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value;
        const trimStr = newQuery.trim();
        if (!trimStr) {
            !!query && setQuery('');
            searchResults.length > 0 && setSearchResults([]);
            return;
        }
        newQuery !== query && setQuery(newQuery);
        // 中文输入法未输入完成时，不触发搜索
        if (!isComposing.current) {
            searchUsers();
        }
    };

    const focus = () => {
        if (inputDom.current) {
            inputDom.current.focus();
        }
    };

    const onBlur = () => {
        setInFocus(false);
        setTimeout(() => {
            setSearchResults([]);
        }, 500);
        props.onBlur && props.onBlur();
    };

    const onfocus = () => {
        setInFocus(true);
        props.onFocus && props.onFocus();
        searchUsers();
    };

    const handleSelectName = (user: Account) => {
        setSelectedCards([...selectedCards, user]);
        setQuery('');
        setSearchResults([]);
    };

    const deleteSelecteds = () => {
        setQuery('');
        setSearchResults([]);
        onDelete && onDelete(selectedCards);
        setSelectedCards([]);
    };

    const onclearSelected = (e: React.MouseEvent | React.TouchEvent) => {
        focus();
        deleteSelecteds();
        // 防止触发input的blur事件
        e.stopPropagation();
        e.preventDefault();
        return false;
    };

    const removeSelectedItem = (item: SELECTED_CARD) => {
        const newSelectedCards = selectedCards.filter((v) => v.account_type !== item.account_type || v.id !== item.id);
        setSelectedCards(newSelectedCards);
        onDelete && onDelete([item]);
    };

    const renderSelected = () => {
        return selectedCards.map((item) => (
            <SelectedItem removeSelectedItem={removeSelectedItem} key={getAccountKey(item)} item={item} />
        ));
    };

    const renderUserResult = () => {
        if (!searchResults.length) {
            return null;
        }
        return (
            <div className="user-results">
                {searchResults.map((user) => (
                    <AccountItem user={user} key={getAccountKey(user)} onSelectUser={handleSelectName} />
                ))}
            </div>
        );
    };

    const hasSelected = selectedCards.length > 0;
    const searchboxCls = classSet({
        'clearfix': true,
        'search-box': true,
        'in-focus': inFocus,
        'has-selected': hasSelected,
    });
    return (
        <div className="member-search-input">
            <div className={searchboxCls} onClick={focus}>
                {renderSelected()}
                <input
                    type="text"
                    value={query}
                    autoFocus={autoFocus}
                    className="search-input"
                    ref={inputRef}
                    onBlur={onBlur}
                    onFocus={onfocus}
                    onChange={handleInputChange}
                    placeholder={hasSelected ? '' : placeholder}
                    onCompositionEnd={onCompositionEnd}
                    onCompositionStart={() => (isComposing.current = true)}
                />
                <If condition={hasCleanBtn && (!!query.trim() || selectedCards.length > 0)}>
                    <button className="clear-btn" onMouseDown={onclearSelected} onTouchEnd={onclearSelected}>
                        <Iconfont type="Delete2" />
                    </button>
                </If>
            </div>
            {renderUserResult()}
        </div>
    );
});
